import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import CupImage from "@/components/merch/CupImage";
import OrderSummary from "@/components/merch/OrderSummary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    CUP_COLORS,
    CUP_BASE_KES,
    CUSTOM_NAME_KES,
    EPL_TEAMS,
    PACK_SIZE,
    STICKER_KES,
    emptyCupLine,
} from "@/data/merchCups";
import { createMerchOrder } from "@/lib/merchApi";
import { normalizeCupLine, pricePerCupLine } from "@/lib/merchPricing";

const STEPS = ["color", "packs", "branding", "details"];

export default function MerchCups() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [colorId, setColorId] = useState(CUP_COLORS[0].id);
    const [packs, setPacks] = useState(1);
    const [cupLines, setCupLines] = useState([]);
    const [customer, setCustomer] = useState({
        name: "",
        phone: "",
        whatsapp: "",
    });
    const [loading, setLoading] = useState(false);

    const color = CUP_COLORS.find((c) => c.id === colorId);
    const cupCount = packs * PACK_SIZE;

    const linesForOrder = useMemo(() => {
        const lines = cupLines.map(normalizeCupLine);
        while (lines.length < cupCount) lines.push(emptyCupLine());
        return lines.slice(0, cupCount);
    }, [cupLines, cupCount]);

    const setLineAt = (index, patch) => {
        setCupLines((prev) => {
            const next = [...prev];
            while (next.length <= index) next.push(emptyCupLine());
            const current = normalizeCupLine(next[index]);
            const merged = { ...current, ...patch };
            if (!merged.team) {
                merged.name = null;
            } else if (patch.name === null) {
                merged.name = null;
            } else if (typeof patch.name === "string") {
                merged.name = patch.name.trim() || null;
            }
            next[index] = merged;
            return next;
        });
    };

    const applyTeamToAll = (teamId) => {
        const team = teamId === "plain" ? null : teamId;
        setCupLines(
            Array.from({ length: cupCount }, (_, i) => {
                const existing = normalizeCupLine(cupLines[i]);
                return {
                    team,
                    name: team ? existing.name : null,
                };
            })
        );
    };

    const canNext = () => {
        if (STEPS[step] === "color") return Boolean(colorId);
        if (STEPS[step] === "packs") return packs >= 1;
        if (STEPS[step] === "branding")
            return linesForOrder.length === cupCount;
        if (STEPS[step] === "details")
            return (
                customer.name.trim() &&
                customer.phone.trim() &&
                customer.whatsapp.trim()
            );
        return false;
    };

    const submitOrder = async () => {
        setLoading(true);
        try {
            const order = await createMerchOrder({
                customer_name: customer.name.trim(),
                phone: customer.phone.trim(),
                whatsapp: customer.whatsapp.trim(),
                color: colorId,
                packs,
                cup_teams: linesForOrder,
            });
            toast.success("Order created — complete M-Pesa payment.");
            navigate(`/merch/order/${order.id}/pay`);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Could not create order. Check your details and try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <main
            data-testid="merch-cups-page"
            className="min-h-screen bg-[#FAFAFA] text-zinc-950"
        >
            <Header />
            <div className="pt-24 sm:pt-28 pb-16 mx-auto max-w-[1100px] px-4 sm:px-6 md:px-10">
                <div className="mb-8 sm:mb-10">
                    <p className="text-sm font-medium text-[#E54D2E] uppercase tracking-wider mb-2">
                        Merch
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight">
                        Branded party cups
                    </h1>
                    <p className="mt-3 text-zinc-600 max-w-2xl">
                        Packs of {PACK_SIZE}. KES {CUP_BASE_KES} per plain cup.
                        Add a team sticker (+KES {STICKER_KES}, total KES{" "}
                        {CUP_BASE_KES + STICKER_KES}) or add a custom name on the
                        sticker (+KES {CUSTOM_NAME_KES} more).
                    </p>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                    {STEPS.map((s, i) => (
                        <span
                            key={s}
                            className={`text-xs font-medium px-3 py-1 rounded-full ${
                                i === step
                                    ? "bg-zinc-950 text-white"
                                    : i < step
                                      ? "bg-zinc-200 text-zinc-700"
                                      : "bg-zinc-100 text-zinc-400"
                            }`}
                        >
                            {i + 1}. {s.charAt(0).toUpperCase() + s.slice(1)}
                        </span>
                    ))}
                </div>

                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                    <div className="space-y-6">
                        {STEPS[step] === "color" && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                {CUP_COLORS.map((c) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        data-testid={`cup-color-${c.id}`}
                                        onClick={() => setColorId(c.id)}
                                        className={`rounded-2xl overflow-hidden border-2 text-left transition-all ${
                                            colorId === c.id
                                                ? "border-[#E54D2E] ring-2 ring-[#E54D2E]/30"
                                                : "border-zinc-200 hover:border-zinc-400"
                                        }`}
                                    >
                                        <CupImage
                                            colorId={c.id}
                                            className="aspect-[4/5] w-full"
                                        />
                                        <span className="block px-3 py-2 text-sm font-semibold">
                                            {c.name}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {STEPS[step] === "packs" && (
                            <div className="rounded-2xl border border-zinc-200 bg-white p-6 space-y-6">
                                <CupImage
                                    colorId={colorId}
                                    className="aspect-[4/5] max-w-[200px] mx-auto rounded-xl"
                                />
                                <div className="flex items-center justify-center gap-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        disabled={packs <= 1}
                                        onClick={() => setPacks((p) => p - 1)}
                                    >
                                        <Minus className="h-4 w-4" />
                                    </Button>
                                    <div className="text-center">
                                        <p className="font-display text-4xl font-bold tabular-nums">
                                            {packs}
                                        </p>
                                        <p className="text-sm text-zinc-500">
                                            pack{packs !== 1 ? "s" : ""} (
                                            {cupCount} cups)
                                        </p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setPacks((p) => p + 1)}
                                    >
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {STEPS[step] === "branding" && (
                            <div className="space-y-4">
                                <p className="text-sm text-zinc-600">
                                    For each cup: skip the sticker (plain), pick a
                                    team, and optionally add a name — or skip the
                                    name.
                                </p>
                                <div className="flex flex-wrap gap-2 items-center">
                                    <Label className="text-sm">
                                        Same sticker on all cups:
                                    </Label>
                                    <Select onValueChange={applyTeamToAll}>
                                        <SelectTrigger className="w-[220px]">
                                            <SelectValue placeholder="Choose option" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="plain">
                                                Skip sticker (plain)
                                            </SelectItem>
                                            {EPL_TEAMS.map((t) => (
                                                <SelectItem
                                                    key={t.id}
                                                    value={t.id}
                                                >
                                                    {t.name} (+KES {STICKER_KES})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <ul className="space-y-4 max-h-[480px] overflow-y-auto pr-1">
                                    {linesForOrder.map((line, i) => (
                                        <li
                                            key={i}
                                            className="rounded-xl border border-zinc-200 bg-white p-4 space-y-3"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-sm font-semibold">
                                                    Cup {i + 1}
                                                </span>
                                                <span className="text-sm font-medium text-[#E54D2E] tabular-nums">
                                                    KES {pricePerCupLine(line)}
                                                </span>
                                            </div>
                                            <div>
                                                <Label className="text-xs text-zinc-500">
                                                    Team sticker
                                                </Label>
                                                <Select
                                                    value={line.team || "plain"}
                                                    onValueChange={(v) =>
                                                        setLineAt(i, {
                                                            team:
                                                                v === "plain"
                                                                    ? null
                                                                    : v,
                                                            name: null,
                                                        })
                                                    }
                                                >
                                                    <SelectTrigger className="mt-1">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="plain">
                                                            Skip sticker — KES{" "}
                                                            {CUP_BASE_KES}
                                                        </SelectItem>
                                                        {EPL_TEAMS.map((t) => (
                                                            <SelectItem
                                                                key={t.id}
                                                                value={t.id}
                                                            >
                                                                {t.name} — KES{" "}
                                                                {CUP_BASE_KES +
                                                                    STICKER_KES}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            {line.team && (
                                                <div className="space-y-2">
                                                    <Label className="text-xs text-zinc-500">
                                                        Custom name on sticker
                                                        (+KES {CUSTOM_NAME_KES})
                                                    </Label>
                                                    <div className="flex flex-col sm:flex-row gap-2">
                                                        <Input
                                                            placeholder="e.g. Alex"
                                                            value={
                                                                line.name || ""
                                                            }
                                                            onChange={(e) =>
                                                                setLineAt(i, {
                                                                    name: e.target
                                                                        .value,
                                                                })
                                                            }
                                                            className="flex-1"
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            className="shrink-0"
                                                            onClick={() =>
                                                                setLineAt(i, {
                                                                    name: null,
                                                                })
                                                            }
                                                        >
                                                            Skip name
                                                        </Button>
                                                    </div>
                                                    <p className="text-xs text-zinc-400">
                                                        Leave blank or tap Skip
                                                        name for sticker only (KES{" "}
                                                        {CUP_BASE_KES +
                                                            STICKER_KES}
                                                        ).
                                                    </p>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {STEPS[step] === "details" && (
                            <form
                                className="rounded-2xl border border-zinc-200 bg-white p-6 space-y-4"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    submitOrder();
                                }}
                            >
                                <div>
                                    <Label htmlFor="name">Full name</Label>
                                    <Input
                                        id="name"
                                        value={customer.name}
                                        onChange={(e) =>
                                            setCustomer((p) => ({
                                                ...p,
                                                name: e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="phone">
                                        M-Pesa phone (2547… or 07…)
                                    </Label>
                                    <Input
                                        id="phone"
                                        inputMode="tel"
                                        value={customer.phone}
                                        onChange={(e) =>
                                            setCustomer((p) => ({
                                                ...p,
                                                phone: e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="whatsapp">
                                        WhatsApp for receipt
                                    </Label>
                                    <Input
                                        id="whatsapp"
                                        inputMode="tel"
                                        value={customer.whatsapp}
                                        onChange={(e) =>
                                            setCustomer((p) => ({
                                                ...p,
                                                whatsapp: e.target.value,
                                            }))
                                        }
                                        required
                                    />
                                </div>
                                <p className="text-xs text-zinc-500">
                                    Payment is M-Pesa only. After you place the
                                    order, you&apos;ll confirm payment on the next
                                    screen.
                                </p>
                                <Button
                                    type="submit"
                                    disabled={loading || !canNext()}
                                    className="w-full rounded-full bg-[#E54D2E] hover:bg-zinc-950"
                                >
                                    {loading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <>
                                            <ShoppingBag className="h-4 w-4 mr-2" />
                                            Place order & pay
                                        </>
                                    )}
                                </Button>
                            </form>
                        )}

                        <div className="flex gap-3">
                            {step > 0 && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="rounded-full"
                                    onClick={() => setStep((s) => s - 1)}
                                >
                                    Back
                                </Button>
                            )}
                            {STEPS[step] !== "details" && (
                                <Button
                                    type="button"
                                    className="rounded-full bg-zinc-950"
                                    disabled={!canNext()}
                                    onClick={() => setStep((s) => s + 1)}
                                >
                                    Continue
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="lg:sticky lg:top-28 h-fit">
                        <OrderSummary
                            packs={packs}
                            cupLines={linesForOrder}
                            colorName={color?.name}
                        />
                    </div>
                </div>
            </div>
            <Footer />
        </main>
    );
}
